import { ReadonlyMat4, mat4, quat, vec2, vec3 } from 'gl-matrix';
import { BoundingBox } from '../BoundingBox';
import { SliceData } from './SliceData';
import { SliceSource } from './SliceSource';
import { Vertex } from './Vertex';
//import { POLYGONS, WINDING_ODD, tesselate } from 'tess2-ts'
import { Edge } from '../Edge';
import { RotatedBoundingBox } from '../RotatedBoundingBox';

/**
 * Type that combines all the parameters that are used to configure the {@link SliceMesh} {@link SliceMesh.generate generation}.
 */
export type SliceMeshGenerationParameters = {
    /**
     * Attribute to control whether {@link SliceData.sliceThickness sliceThickness} should be considered in {@link SliceMesh} creation.\
     * If the {@link SliceData} defines {@link SliceData.sliceThickness sliceThickness}, each slice will represent a cuboid instead of a simple {@link SliceData Rectangle}.\
     * Generally having this attribute enabled makes very little difference.\
     * It only impacts the slices that are not connected to other slices.\
     * If {@link SliceSource} has constant SliceSpacing and no missing slices, only the first and last slices are impacted.\
     * If there are missing slices then all of the gaps are affected.
     */
    considerThickness: boolean;

    /**
     * Attribute to control whether the gaps formed by missing slices should be interpolated or left blank.\
     * Generally it is better to leave the gaps blank\
     * so the user knows that there are missing slices and does not base their decision making on interpolated data.\
     * But for other than image or contour data, filling the gaps might be needed.\
     * So having that possibility is necessary.
     */
    fillGaps: boolean;
};

/**
 * {@link SliceMesh} is a 3D volume consisting of cuboids.
 *
 * {@link SliceMesh} is built of Slices.\
 * There are two types of {@link SliceData Slices};\
 * {@link SliceData Slices} with and Slices without sliceThickness.\
 * {@link SliceData Slices} that do have thickness can be thought of as 3D cuboids.\
 * While slices without thickness can be thought of as planar 2D {@link SliceData rectangles} in 3D space.\
 * All slices share the same normal direction and planar dimensions.\
 * Only differing in coordinates along that normal direction.
 *
 * {@link SliceMesh} objects also contains 3D texture data, resembling the contents of the volume.\
 * And so the {@link SliceMesh} contains data used to map that texture on the mesh.\
 * This mapping is handled by textureCoordinates.
 *
 * If we only focus on slices without slice thickness,\
 * two consecutive slices can be thought of as the bottom face and top face of a 3D cuboid.\
 * That is the format in which the data in {@link SliceMesh} is stored.\
 * As 3D cuboids constructed of consecutive slices.
 *
 * While creating cuboid between every two slices would be possible, it's not feasible. That's a naive approach.
 * There's room for optimization. Note: Only when working with interpolated data!
 *
 * Each of those cuboids consist of 8 vertices; 4 on the bottom slice and 4 on the top slice.\
 * And each of those vertices consists of 3 patient space coordinates and 3 texture coordinates.
 *
 * The {@link SliceMesh} is used to visualize data made up of slices.\
 * As the texture data is made up of pixels that may have relatively low resolution, to improve the visualization, the data is commonly interpolated.\
 * When working with interpolated data, we are only interested in changes in the texture resolution. The resolution is defined as px/mm.\
 * When interpolating, if the height of two consecutive cuboids is equal, they may be combined into one, as the resolution stays constant between them.\
 * And that way we can reduce the complexity of the mesh. Reducing it's memory consumption and improving the performance by having simpler data.
 *
 * In addition to all of this {@link SliceMesh} implements {@link SliceSourceListener}.\
 * This makes it possible for {@link SliceMesh} to register itself as a listener to it's {@link sliceSource}.\
 * So whenever the {@link SliceSource} is updated, the {@link SliceMesh} gets notified and updates itself aswell.
 */
export class SliceMesh implements Iterable<SliceData> {
    /**
     * Parameters that define how the SliceMesh is generated.\
     * This consists of:\
     * 1. {@link SliceMeshGenerationParameters.considerThickness considerThickness}, which is a boolean,
     * controlling whether {@link SliceData.sliceThickness sliceThickness} should be taken into account.
     * 2. {@link SliceMeshGenerationParameters.fillGaps fillGaps}, which is also a boolean,
     * controlling whether the empty space in-between slices should be filled or not.
     */
    public readonly meshGenerationParameters: SliceMeshGenerationParameters;

    /**
     * The {@link SliceSource} this {@link SliceMesh} is generated from.
     */
    private readonly _sliceSource: SliceSource<SliceData>;

    /**
     * {@link transformationMatrix} is a {@link mat4 4 by 4 matrix} that defines the transformation of point data to PatientSpace.\
     * The point data should be {@link ReadonlyVec3 vec3} in format {[-1, 1], [-1, 1], {@link SliceData.normalPosition normalPosition}}.
     */
    private transformationMatrix: mat4;

    /**
     * Constructor that will {@link generate} a new {@link SliceMesh} from {@link sliceSource}.\
     * The constructor offers many parameters that can be used to control the {@link SliceMesh} generation.
     * @param sliceSource The {@link SliceSource} from which the {@link SliceMesh} is to be generated.
     * @param parameters {@link SliceMeshGenerationParameters} Defining parameter values that should be used for generation.
     */
    public constructor(
        sliceSource: SliceSource<SliceData>,
        parameters: SliceMeshGenerationParameters
    ) {
        this._sliceSource = sliceSource;
        this.meshGenerationParameters = parameters;
        if (parameters.fillGaps) {
            console.warn(
                `Using SliceMesh with fillGaps, which is not yet properly supported.`
            );
        }

        //generate matrix that transforms points of type {[-1, 1], [-1, 1], [positionInNormalDirection]} to PatientSpaceCoordinates
        this.transformationMatrix = mat4.create();
        this.updateTransformation();
    }

    /**
     * Method to force update the {@link transformationMatrix} of this {@link sliceMesh}.\
     * After update the {@link transformationMatrix} should present transformation from ImageSpace x & y coordinates +
     * ImageSpace aligned normalPosition to PatientSpace coordinates. ([-1, 1], [-1, 1], normalPosition) -> PatientSpace.
     */
    public updateTransformation(): void {
        const rotation = quat.create();
        quat.fromMat3(rotation, this._sliceSource.rotation);

        const transformationMatrix = mat4.create();
        const center = vec3.clone(this._sliceSource.center);

        const imgCenter = vec3.transformMat3(
            vec3.create(),
            center,
            this._sliceSource.invRotation
        );
        const planarImageCenter = vec3.fromValues(
            imgCenter[0],
            imgCenter[1],
            0
        );
        const planarPatientCenter = vec3.transformMat3(
            vec3.create(),
            planarImageCenter,
            this._sliceSource.rotation
        );

        const scale = vec3.fromValues(
            this._sliceSource.widthMm * 0.5,
            this._sliceSource.heightMm * 0.5,
            1
        );

        mat4.fromRotationTranslationScale(
            transformationMatrix,
            rotation,
            planarPatientCenter,
            scale
        );
        mat4.copy(this.transformationMatrix, transformationMatrix);
    }

    public get numberOfSlices(): number {
        return this._sliceSource.numberOfSlices;
    }

    /**
     * Method to get the optimal {@link RotatedBoundingBox} for this {@link SliceMesh}.\
     * The {@link BoundingBox} is always generated from the 8 edge coordinates.\
     * Rotation is derived from the {@link sliceSource}, as it is its {@link SliceSource.invRotation invRotation}.
     */
    public get boundingBox(): RotatedBoundingBox {
        const bb = new BoundingBox();
        bb.expandWithPoint(
            vec3.transformMat3(
                vec3.create(),
                this._sliceSource.center,
                this._sliceSource.invRotation
            )
        );
        bb.addMargins(
            vec3.fromValues(
                this._sliceSource.widthMm * 0.5,
                this._sliceSource.heightMm * 0.5,
                0
            )
        );
        if (!bb.min || !bb.max) throw new Error('Invalid bounding box');
        bb.min[2] = this._sliceSource.sliceIndexToNormalPosition(0);
        bb.max[2] = this._sliceSource.sliceIndexToNormalPosition(
            this._sliceSource.numberOfSlices - 1
        );
        return new RotatedBoundingBox(bb, this._sliceSource.rotation);
    }

    /**
     * Custom tessellation function that will somewhat efficiently tessellate convex polygons.\
     * This computes the centroid (average of all the points) that make up the polygon.\
     * Subtracts that point from all the points of the polygon.\
     * Calculates the orientation of that point relative to the center using {@link Math.atan2}.\
     * Sorts the points based on their rotation.\
     * And constructs an index buffer based on that ordering.
     * @param vertexCoordinates {@link Float32Array} containing all the vertex coordinates.
     * @param startIndex The index value that the very first point should have.\
     * As the {@link SliceMesh} may be made up of multiple sections that are not connected to one another,\
     * but still want to save them into a single {@link VertexArrayObject} as those meshes should always be rendered together.\
     * So we {@link tesselate} them separately starting the second structure from where the first one left off.
     * @returns An {@link Array} of indices forming primitives of type {@link WebGL2RenderingContext.TRIANGLES}.
     */
    public static tesselate(
        vertexCoordinates: Float32Array,
        startIndex: number
    ): number[] {
        const numberOfPoints = vertexCoordinates.length / 2;
        if (numberOfPoints > 2) {
            const total = vec2.create();
            let i = 0;
            while (i < vertexCoordinates.length) {
                total[0] += vertexCoordinates[i++];
                total[1] += vertexCoordinates[i++];
            }
            const centroid = vec2.scale(
                vec2.create(),
                total,
                1 / numberOfPoints
            );

            const points: { orientation: number; index: number }[] = new Array<{
                orientation: number;
                index: number;
            }>(numberOfPoints);
            //calculate orientation of each index
            i = 0;
            let index = 0;
            while (i < vertexCoordinates.length) {
                const x = vertexCoordinates[i++] - centroid[0];
                const y = vertexCoordinates[i++] - centroid[1];
                //I tried optimized atan2 https://mazzo.li/posts/vectorized-atan2.html, seems javascript's default is equally fast. WASM might be faster
                points[index] = {
                    orientation: Math.atan2(y, x),
                    index: startIndex + index,
                };
                ++index;
            }
            points.sort(
                (
                    a: { orientation: number; index: number },
                    b: { orientation: number; index: number }
                ): number => {
                    return a.orientation - b.orientation;
                }
            );
            const indices = new Array<number>((numberOfPoints - 2) * 3);
            const last = points[numberOfPoints - 1];
            let currentIndex = 0;
            for (let a = 1, b = numberOfPoints - 1; a < b; ++a) {
                indices[currentIndex++] = points[a - 1].index;
                indices[currentIndex++] = points[a].index;
                indices[currentIndex++] = last.index;
            }

            return indices;
        } else if (numberOfPoints > 0) {
            return [0, numberOfPoints === 2 ? 1 : 0, 0];
        }
        return [];
    }

    /**
     * Method to get the intersection points for a single section of the {@link SliceMesh}.
     * Section may consist of either 4 edges if it's just a single slice without any thickness.
     * Or 12 edges if it consists of multiple slices or has a thickness.
     * @param edges An {@link Array} of 4 or 12 {@link Edge Edges} that the section is made up of.
     * @param toProjection {@link ReadonlyMat4 TransformationMatrix} from PatientSpace to ProjectionSpace.\
     * All the {@link edges} are stored within PatientSpace and need to be converted to ProjectionSpace for intersections.
     * @returns An {@link Array} of {@link Vertex Vertices} that present all the intersection points.
     */
    public static intersections(
        edges: Edge[],
        toProjection: ReadonlyMat4
    ): Vertex[] {
        const intersections = new Array<Vertex>();
        let intersection: Vertex | undefined;
        for (const edge of edges) {
            if (
                (intersection = edge.intersection(
                    toProjection,
                    edges.length === 4
                ))
            ) {
                intersections.push(intersection);
            }
        }
        return intersections;
    }

    /**
     * Method to get an {@link Symbol.iterator iterator} for this {@link SliceMesh}.\
     * This will return an {@link Iterator} that iterates over all the {@link SliceData slices} of the {@link SliceMesh}.\
     * @returns An {@link Iterator} that iterates over all the {@link SliceData slices} of the {@link SliceMesh}.
     */
    [Symbol.iterator](): Iterator<SliceData, SliceData, undefined> {
        return this._sliceSource[Symbol.iterator]();
    }
}
